// Interviews v2 - tiny fetch layer shared by the dialog, the page and the candidate card.
// Every error is turned into a STRING before it reaches toast(): the white screen on the
// candidate page was React being handed FastAPI's 422 `detail` array as a toast child.

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const BASE = `${API_URL}/api/interviews-v2`;

export function errorText(detail, fallback = 'Something went wrong') {
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((d) => (typeof d === 'string' ? d : `${(d.loc || []).slice(-1)[0] || 'field'}: ${d.msg || JSON.stringify(d)}`))
      .join('; ') || fallback;
  }
  if (typeof detail === 'object') {
    if (detail.message) return String(detail.message);
    if (detail.detail) return errorText(detail.detail, fallback);
    try { return JSON.stringify(detail); } catch (e) { return fallback; }
  }
  return String(detail);
}

function headers() {
  const token = localStorage.getItem('ats_token');
  return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

async function call(method, path, body, params) {
  let url = `${BASE}${path}`;
  if (params) {
    const qs = Object.entries(params)
      .filter(([, v]) => v !== undefined && v !== null && v !== '')
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
      .join('&');
    if (qs) url += (url.includes('?') ? '&' : '?') + qs;
  }
  const res = await fetch(url, { method, headers: headers(), body: body === undefined ? undefined : JSON.stringify(body) });
  let data = null;
  const text = await res.text();
  try { data = text ? JSON.parse(text) : null; } catch (e) { data = { detail: text }; }
  if (!res.ok) {
    const err = new Error(errorText(data && data.detail, `Request failed (${res.status})`));
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

export const interviewsV2 = {
  status: () => call('GET', '/status'),
  summary: () => call('GET', '/summary'),
  list: (params) => call('GET', '', undefined, params),
  get: (id) => call('GET', `/${id}`),
  forCandidate: (candidateId) => call('GET', `/candidate/${candidateId}`),
  contacts: (params) => call('GET', '/contacts', undefined, params),
  conflicts: (params) => call('GET', '/conflicts', undefined, params),
  create: (body) => call('POST', '', body),
  reschedule: (id, body) => call('POST', `/${id}/reschedule`, body),
  cancel: (id, body) => call('POST', `/${id}/cancel`, body),
  complete: (id, body) => call('POST', `/${id}/complete`, body),
  resendInvite: (id) => call('POST', `/${id}/resend-invite`),
  runReminders: () => call('POST', '/reminders/run'),
  icsUrl: (id) => `${BASE}/${id}/ics`,
  async downloadIcs(id) {
    const res = await fetch(`${BASE}/${id}/ics`, { headers: headers() });
    if (!res.ok) throw new Error(`Could not fetch the .ics (${res.status})`);
    const blob = await res.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `interview-${id.slice(0, 8)}.ics`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  },
  async users() {
    const res = await fetch(`${API_URL}/api/users`, { headers: headers() });
    if (!res.ok) return [];
    const data = await res.json();
    const arr = Array.isArray(data) ? data : data.users || [];
    return arr.filter((u) => u && u.id).map((u) => ({ id: u.id, full_name: u.full_name || u.name || u.email, email: u.email, role: u.role }));
  },
  async requirements(params) {
    const qs = new URLSearchParams(Object.entries(params || {}).filter(([, v]) => v)).toString();
    const res = await fetch(`${API_URL}/api/requirements${qs ? `?${qs}` : ''}`, { headers: headers() });
    if (!res.ok) return [];
    const data = await res.json();
    return Array.isArray(data) ? data : data.requirements || data.items || [];
  },
};

export const MODE_LABEL = { in_person: 'In person', video: 'Video', phone: 'Phone' };
export const STATUS_STYLE = {
  scheduled: 'bg-blue-50 text-blue-700 border-blue-200',
  completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  cancelled: 'bg-slate-100 text-slate-600 border-slate-200',
  no_show: 'bg-amber-50 text-amber-800 border-amber-200',
};
export const OUTCOME_LABEL = {
  selected: 'Selected', rejected: 'Rejected', on_hold: 'On hold', next_round: 'Next round', no_show: 'No-show', pending: 'Feedback pending',
};

// IST helpers - the backend stores UTC ISO; the recruiter thinks in IST.
const IST_OFFSET_MIN = 330;
export function toIstParts(iso) {
  if (!iso) return { date: '', time: '' };
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { date: '', time: '' };
  const ist = new Date(d.getTime() + (IST_OFFSET_MIN + d.getTimezoneOffset()) * 60000);
  const pad = (n) => String(n).padStart(2, '0');
  return {
    date: `${ist.getFullYear()}-${pad(ist.getMonth() + 1)}-${pad(ist.getDate())}`,
    time: `${pad(ist.getHours())}:${pad(ist.getMinutes())}`,
  };
}
export function fromIstParts(date, time) {
  // "YYYY-MM-DD" + "HH:MM" in IST -> ISO with the +05:30 offset (the server reads offsets)
  if (!date || !time) return null;
  return `${date}T${time}:00+05:30`;
}
export function fmtIst(iso, opts) {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', weekday: 'short', day: '2-digit', month: 'short',
    hour: '2-digit', minute: '2-digit', hour12: true, ...(opts || {}) });
}
export function fmtIstTime(iso) {
  if (!iso) return '-';
  return new Date(iso).toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', hour12: true });
}
export function isToday(iso) {
  const a = toIstParts(iso).date;
  return a && a === toIstParts(new Date().toISOString()).date;
}
